/* Built In Imports */
import NextLink from 'next/link';

/* External Imports */
import { Box, Link, Text, useMediaQuery } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { nanoid } from 'nanoid';
import Slider from 'react-slick';

/* Internal Imports*/
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { refineUrlDomain } from '@components/Utility/utils';
import config from '@config';
import { useRouter } from 'next/router';

/* Services */

/**
 * Renders the Card Info With Multiple Image Detail
 * @param {Object} data - data for section
 * @param {string} region - Region for section
 * @param {string} lang - Language for section
 * @param {Object} index - index for cards
 * @returns {ReactElement} - CardInfoWithDetailsAndProgress card component
 */
const CardInfoWithDetailsAndProgress = ({
    sectionContent,
    section,
    data,
    region,
    lang,
    index,
    height,
    width
}) => {
    // const outreactZigZagSection has been created just to add specific styling for the in/en/outreach page
    const outreactZigZagSection = section.sectionId === 'zigzag-section';
    let otpZigZagCard = section.sectionId === 'journey-overview'
    // console.log({outreactZigZagSection})

    const isMobile = useMediaQuery({ maxWidth: '480px' })?.[0];
    // console.log("Ismobile:", isMobile);


    const settings = {
        dots: true,
        autoplay: true,
        arrows: false,
        infinite: true,
        speed: 1000,
        slidesToShow: 1,
        slidesToScroll: 1,

        customPaging: function () {
            return (
                <Text
                    cursor="pointer"
                    display="inline-block"
                    w="12px"
                    h="12px"
                    bgColor="#fff"
                    borderRadius="50%"
                ></Text>
            );
        },
    };

    let isEvenIndex = index % 2 !== 0;
    const flexDirection = isEvenIndex ? 'row-reverse' : 'row';
    const textAlign = 'left';
    const float = isEvenIndex ? 'right' : 'left';
    const router = useRouter();
    const volunteerUrl = router.asPath.includes('/volunteer');

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems={otpZigZagCard ? 'center' : undefined}
            key={nanoid()}
            mb={{ base: '0px', md: '0px', lg: '4.5rem' }}
            flexDirection={{ base: 'column-reverse', md: 'column-reverse', lg: flexDirection }}
        >
            <Box
                className="slider description"
                w={{ base: '100%', md: '100%', lg: '50%' }}
                color={'black'}
                textAlign="center"
                height="auto"
                margin={volunteerUrl ? 'auto' : '0'}
                display={'flex'}
                justifyContent={'center'}
            >
                <Box
                    fontFamily={'FedraSansStd-book'}
                    textAlign={{ base: 'center', md: 'center', lg: textAlign }}
                    className="structuredText"
                    color={'#fff'}
                    width={{ base: '70%', md: '100%', lg: '247px' }}
                    marginTop={{ base: '-5px', md: '-5px', lg: '20px' }}
                >
                    <StructuredTextParser
                        str={render(data.description)}
                        region={region}
                        lang={lang}
                        // textAlign={textAlign}
                        textAlign={{ base: 'center', md: 'center', lg: textAlign }}
                        color={'#fff'}
                        fontSize={{ base: '16px', md: '16px', lg: '18px' }}
                        lineHeight={'24px'}
                    />

                    {data.buttonLink && (
                        <Box>
                            <NextLink
                                onClick={e => refreshPage(e?.target?.href, e)}
                                href={refineUrlDomain(data.buttonLink, region, lang)}
                                passHref
                                legacyBehavior
                            >
                                <Link
                                    bgColor="transparent"
                                    color="#cf4520"
                                    mr={{ md: '20px' }}
                                    padding="5px 30px"
                                    border="1px solid #cf4520"
                                    borderRadius="5px"
                                    display="inline-block"
                                    my={19}
                                    fontFamily="FedraSansStd-medium"
                                    fontSize="13px"
                                    _hover={{
                                        color: '#fff',
                                        bgColor: '#cf4520',
                                        textDecoration: 'none',
                                    }}
                                    _focus={{
                                        color: '#fff',
                                        bgColor: '#cf4520',
                                        textDecoration: 'none',
                                    }}
                                    _active={{
                                        color: '#fff',
                                        bgColor: '#cf4520',
                                        textDecoration: 'none',
                                    }}
                                    target={
                                        data?.buttonLink.indexOf('http') > -1 &&
                                            data?.buttonLink.indexOf(config.cdnPath) < 0
                                            ? '_blank'
                                            : ''
                                    }
                                >
                                    {data.buttonText}
                                </Link>
                            </NextLink>
                        </Box>
                    )}
                </Box>
                <style jsx global>{`
                    .structuredText strong {
                        color: #fff;
                        font-size: 24px;
                        line-height: 35px;
                    }
                    .structuredText p {
                        padding-bottom: 0px;
                    }
                    .structuredText a:hover {
                        border-bottom: 1px solid #cf4824;
                    }

                    /* Mobile Styles */
                    @media (max-width: 767px) {
                        .structuredText strong {
                        font-size: 20px;
                        line-height: 25px;
                        }
                        .structuredText p {
                        padding-bottom: 5px;
                        }
                    }
                `}</style>

            </Box>
            <Box
                width={{ base: '100%', md: '100%', lg: '28rem' }}
                paddingTop={{ base: '0px', md: '0px', lg: '2.5rem' }}
                justifyContent={{ base: 'center', md: 'center', lg: !isEvenIndex ? 'left' : 'right' }}
                display={{ base: index / 1 == 0 ? 'none' : 'flex', md: index / 1 == 0 ? 'none' : 'flex', lg: 'flex' }}
            >
                <LazyLoadImageComponent
                    display={{ base: 'block', md: 'block', lg: 'none' }}
                    src={isEvenIndex ? 'https://www.datocms-assets.com/46272/1747979189-otp-journey-overview-zigzag-line-mob-left.png' :
                        'https://www.datocms-assets.com/46272/1747979242-otp-journey-overview-zigzag-line-mob-right.png'
                    }
                />
            </Box>
        </Box>
    );
};

export default CardInfoWithDetailsAndProgress;
